import { CSSProperties, useEffect, useRef } from 'react'
import { CancellationError } from '@st/util/job-queue'
import { MuPDFRendererBackend } from './hooks/MuPDFViewerBackend'

type Props = {
  backend: MuPDFRendererBackend
  className?: string
  pageNumber: number
  width: number
  height: number
  style?: CSSProperties
  onLoad?: () => void
}

export function CanvasImage({
  backend,
  className,
  pageNumber,
  width,
  height,
  style,
  onLoad
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    return () => backend.cancelRender(pageNumber)
  }, [backend, pageNumber])

  useEffect(() => {
    let cancelled = false

    backend
      .renderPage(pageNumber)
      .then((bitmap) => {
        // once we write to the canvas, we no longer need the bitmap
        if (cancelled || !canvasRef.current) {
          bitmap.close()
          return
        }

        // Prefer zero-copy transfer when supported
        const bitmapCtx = canvasRef.current.getContext(
          'bitmaprenderer'
        ) as ImageBitmapRenderingContext | null
        if (bitmapCtx) {
          bitmapCtx.transferFromImageBitmap(bitmap)
          // bitmap is now detached; no further use possible
        } else {
          canvasRef.current.getContext('2d')?.drawImage(bitmap, 0, 0, width, height)
          bitmap.close() // release when we copied instead of transferred
        }

        onLoad?.()
      })
      .catch((err) => {
        if (err instanceof CancellationError) return
        // Non-cancellation errors can surface for now
        throw err
      })

    // Cleanup for unmount / hot-refresh
    return () => {
      cancelled = true
    }
  }, [backend, pageNumber, width, height, onLoad])

  return (
    <canvas ref={canvasRef} width={width} height={height} style={style} className={className} />
  )
}
